import { z } from 'zod';

export type ReportTarget = 'REVIEW' | 'PROFILE';
export const reportReasons = [
  { value: 'SPAM', label: 'Spam or advertising', detail: 'Promotions, links or repeated content.', targets: ['REVIEW', 'PROFILE'] },
  { value: 'OFFENSIVE', label: 'Offensive or abusive', detail: 'Harassment, hate or threats.', targets: ['REVIEW', 'PROFILE'] },
  { value: 'MISLEADING', label: 'Not a genuine review', detail: 'Fake, copied or about a different fragrance.', targets: ['REVIEW'] },
  { value: 'PERSONAL_INFO', label: 'Personal information', detail: 'Someone’s private details are shared.', targets: ['REVIEW', 'PROFILE'] },
  { value: 'INAPPROPRIATE_IMAGE', label: 'Inappropriate picture', detail: 'The profile picture is explicit or offensive.', targets: ['PROFILE'] },
  { value: 'OTHER', label: 'Something else', detail: 'Tell us what’s wrong below.', targets: ['REVIEW', 'PROFILE'] },
] as const;
export type ReportReason = (typeof reportReasons)[number]['value'];
export const reportReasonLabel = Object.fromEntries(reportReasons.map((r) => [r.value, r.label])) as Record<ReportReason, string>;

export const reportSchema = z
  .object({
    target: z.enum(['REVIEW', 'PROFILE']),
    id: z.string().trim().min(1).max(100),
    reason: z.enum(['SPAM', 'OFFENSIVE', 'MISLEADING', 'PERSONAL_INFO', 'INAPPROPRIATE_IMAGE', 'OTHER']),
    details: z.string().trim().max(1000, 'Keep the details under 1,000 characters.').default(''),
    // Honeypot: people never see this field.
    website: z.string().max(200).optional(),
  })
  .superRefine((value, context) => {
    const reason = reportReasons.find((r) => r.value === value.reason)!;
    if (!(reason.targets as readonly string[]).includes(value.target))
      context.addIssue({ code: 'custom', path: ['reason'], message: 'Choose a reason that fits.' });
    if (value.reason === 'OTHER' && value.details.length < 10)
      context.addIssue({ code: 'custom', path: ['details'], message: 'Tell us what’s wrong in a few words.' });
  });
export type ReportInput = z.infer<typeof reportSchema>;
export const reportActions = ['DISMISS', 'REMOVE_REVIEW', 'REMOVE_AVATAR', 'MAKE_PRIVATE'] as const;
export type ReportAction = (typeof reportActions)[number];
